/* eslint-disable prettier/prettier */
import * as Yup from 'yup';

import { TRIP_STATUS_CODES } from '@/constants';

export const ROLE_BACKGROUND_COLOR = {
  admin: '#ecfdf4',
  user: '#F7F7F8',
};

export const ROUND_MILES_OPTIONS = [
  { value: `ceiling`, key: `Round Up` },
  { value: `floor`, key: `Round Down` },
];

export const REIMBURSEMENT_RATE_RESULTS_PER_PAGE_OPTIONS = [
  { label: '2 results', value: 2 },
  { label: '5 results', value: 5 },
];

export const TRIP_TYPE_TO_FORM_NAME = {
  mileageReimbursement: 'Mileage Reimbursement',
  publicTransport: 'Public Transport',
  ancillaryServices: 'Ancillary Services',
};

export const SYSTEM_LABELS = Object.freeze({
  HEALTH_APP_USER: 'Health App User',
});

export const HIDDEN_LABEL_GROUPS = Object.freeze(['System']);


export const USAGE_QUALIFIER_TO_ANCILLARY_SERVICE = {
  'ancillary.meals': 'Meals',
  'ancillary.lodging': 'Lodging',
  'ancillary.flights': 'Flights',
  'ancillary.parking': 'Parking',
  'ancillary.tolls': 'Tolls',
};

export const REIMBURSEMENT_RATE_INITIAL_VALUES = {
  procedureCode: '',
  mileageRate: null,
  ancillaryService: '',
  modifiers: [],
  startDate: '',
  endDate: '',
};

export const getReimbursementRatesValidationSchema = (tripType) =>
  Yup.object().shape({
    procedureCode: Yup.string()
      .trim()
      .min(1, 'Cannot be only whitespaces')
      .max(100, 'Length cannot be greater than 100')
      .required('Procedure code is required'),
    mileageRate: Yup.number()
      .when('isMileageRateRequired', (_, schema) => {
        if (tripType === 'mileageReimbursement')
          return schema
            .required('Mileage rate is required')
            .test('greaterThanZero', 'Must be greater than 0', (value) => {
              if (value > 0) return true;
              return false;
            });
        return schema;
      })
      .nullable()
      .typeError('Please enter a valid number'),
    ancillaryService: Yup.string().when('isAncillaryServiceRequired', {
      is: tripType === 'ancillaryServices',
      then: Yup.string().required('Ancillary service is required'),
      otherwise: Yup.string(),
    }),
    modifiers: Yup.array()
      .of(
        Yup.string()
          .length(2, 'Length must be 2')
          .matches(/^[a-zA-Z0-9]+$/, 'Invalid character'),
      )
      .min(0)
      .max(4),
    startDate: Yup.date()
      .nullable()
      .typeError('Enter a valid date in the format MM/DD/YYYY')
      .required('Start date is required'),
    endDate: Yup.date()
      .nullable()
      .typeError('Enter a valid date in the format MM/DD/YYYY')
      .required('End date is required')
      .when('startDate', (startDate, schema) =>
        startDate
          ? schema.test(
            'startDateBeforeEndDate',
            'End date must be after start date',
            async (endDate) => {
              const isValid = await Yup.date().isValid(startDate);
              if (isValid && endDate)
                return new Date(endDate) >= new Date(startDate);
              return schema;
            },
          )
          : schema,
      ),
  });

export const ASSIGNMENT_WINDOW_OPTIONS = {
  every7Days: { title: '7 Days', days: 7 },
  every14Days: { title: '14 Days', days: 14 },
  every21Days: { title: '21 Days', days: 21 },
  every30Days: { title: '30 Days', days: 30 },
};

export const WINDOW_TO_PAUSE_AUTO_ASSIGNMENT_OPTIONS = {
  oneDay: { title: '1 Day', days: 1 },
  twoDay: { title: '2 Days', days: 2 },
  threeDay: { title: '3 Days', days: 3 },
  fourDay: { title: '4 Days', days: 4 },
  fiveDay: { title: '5 Days', days: 5 },
  // sixDay: { title: '6 Days', days: 6 },
  // sevenDay: { title: '7 Days', days: 7 },
};

export const PROVIDER_NO_SHOW_AUTOMATION = {
  threeDay: { title: '3 Days', days: 3 },
  sevenDay: { title: '7 Days', days: 7 },
  fourteenDay: { title: '14 Days', days: 14 },
};

export const RECURRING_ITINERARY_AUTO_ASSIGNMENT_OVERRIDE = [
  { displayText: 'None', value: 'OFF' },
  {
    displayText: `Apply first itinerary's assignment to rest of schedule`,
    value: 'DEFER_UNTIL_AMBASSADOR_ITINERARY_RESOLUTION',
  },
];

export const AUTO_REVERTABLE_ASSIGNMENT_STATUSES = [
  {
    displayText: 'Assignment Error',
    value: TRIP_STATUS_CODES.ASSIGNMENT_ERROR,
  },
  {
    displayText: 'Assignee Unavailable',
    value: TRIP_STATUS_CODES.ASSIGNMENT_UNAVAILABLE,
  },
  {
    displayText: 'Assignee Cancelled',
    value: TRIP_STATUS_CODES.ASSIGNMENT_CANCELLED,
  },
];

export const ELIGIBILITY_CRITERIA_CONFIG = [
  {
    field: 'maxWalkingDistanceToPickup',
    title: 'Max Walking Distance to Pickup',
    subtitle:
      'Warn users if walking distance to the pickup location exceeds this value',
    type: 'number',
    label: '# of Miles',
  },
  {
    field: 'maxWalkingDistanceFromDropoff',
    title: 'Max Walking Distance from Drop-off',
    subtitle:
      'Warn users if walking distance from the drop-off location exceeds this value',
    type: 'number',
    label: '# of Miles',
  },
  {
    field: 'maxTransfers',
    title: 'Max Number of Transfers',
    subtitle: 'Warn users if the route requires more transfers than this value',
    type: 'number',
    label: '# of Transfers',
  },
  {
    field: 'minEligibleAge',
    title: 'Minimum Eligible Age',
    subtitle: 'Warn users if the member is younger than this age',
    type: 'number',
    label: 'Min Age',
  },
  {
    field: 'maxEligibleAge',
    title: 'Maximum Eligible Age',
    subtitle: 'Warn users if the member is older than this age',
    type: 'number',
    label: 'Max Age',
  },
  {
    field: 'ineligibleAppointmentReasons',
    title: 'Ineligible Appointment Reasons',
    subtitle:
      'Members with these appointment reasons are not eligible for public transportation',
    type: 'select',
    label: 'Reasons',
  },
  {
    field: 'maxTripDurationInMinutes',
    title: 'Max Trip Duration',
    subtitle: 'Warn users if the trip duration exceeds this value',
    type: 'number',
    label: 'Max Minutes',
  },
];

export const ELIGIBILITY_RULE_INITIAL_VALUES = {
  maxWalkingDistanceToPickup: null,
  maxWalkingDistanceFromDropoff: null,
  maxTransfers: null,
  minEligibleAge: null,
  maxEligibleAge: null,
  ineligibleAppointmentReasons: [],
  maxTripDurationInMinutes: null,
};

export const ELIGIBILITY_RULE_ID = {
  maxWalkingDistanceToPickup: 'MAX_WALKING_DISTANCE_TO_PICKUP_RULE',
  maxWalkingDistanceFromDropoff: 'MAX_WALKING_DISTANCE_FROM_DROPOFF_RULE',
  maxTransfers: 'MAX_NUMBER_OF_TRANSFERS_RULE',
  maxTripDurationInMinutes: 'MAX_TRIP_DURATION_RULE',
  minEligibleAge: 'MIN_ELIGIBLE_AGE_RULE',
  maxEligibleAge: 'MAX_ELIGIBLE_AGE_RULE',
  ineligibleAppointmentReasons: 'INELIGIBLE_APPOINTMENT_REASON_RULE',
};

export const ELIGIBILITY_RULE_FACT = {
  ineligibleAppointmentReasons: 'trip.appointmentReason',
  maxTransfers: 'trip.numberOfTransfers',
  maxWalkingDistanceToPickup: 'trip.walkingDistanceToPickupInMiles',
  maxWalkingDistanceFromDropoff: 'trip.walkingDistanceFromDropoffInMiles',
  maxTripDurationInMinutes: 'trip.tripDurationInMinutes',
  minEligibleAge: 'member.age',
  maxEligibleAge: 'member.age',
};

export const ELIGIBILITY_RULE_OPERATOR = {
  ineligibleAppointmentReasons: 'containsAny',
  maxWalkingDistanceFromDropoff: 'greaterThan',
  maxWalkingDistanceToPickup: 'greaterThan',
  maxTransfers: 'greaterThan',
  maxTripDurationInMinutes: 'greaterThan',
  minEligibleAge: 'lessThan',
  maxEligibleAge: 'greaterThan',
};

export const ELIGIBILITY_RULE_EVENT_TYPE = {
  maxEligibleAge: 'MAX_ELIGIBLE_AGE_VIOLATION',
  default: 'PUBLIC_TRANSPORTATION_ELIGIBILITY_RULE_VIOLATION',
};

export const ELIGIBILITY_RULE_EVENT_MESSAGE = {
  ineligibleAppointmentReasons:
    'Appointment reason selected is not eligible for public transportation',
  maxWalkingDistanceToPickup:
    'Pickup walking distance exceeds the allowed limit',
  maxWalkingDistanceFromDropoff:
    'Drop-off walking distance exceeds the allowed limit',
  maxTransfers: 'Too many transit transfers required',
  maxTripDurationInMinutes: 'Max trip duration exceeds the allowed limit',
  minEligibleAge: 'Member is below eligible age',
  maxEligibleAge: 'Member exceeds the eligible age',
};

export const ELIGIBILITY_RULE_MAX_VALUES = {
  maxWalkingDistanceToPickup: 10000000,
  maxWalkingDistanceFromDropoff: 10000000,
  maxTransfers: 10000000,
  maxEligibleAge: 1000,
  maxTripDurationInMinutes: 10000000,
};

export const ELIGIBILITY_RULE_INTEGER_ONLY_FIELDS = [
  'minEligibleAge',
  'maxEligibleAge',
  'maxTransfers',
];

export const ELIGIBILITY_RULE_UNITS = {
  maxWalkingDistanceToPickup: 'miles',
  maxWalkingDistanceFromDropoff: 'miles',
  maxTripDurationInMinutes: 'minutes',
};

/**
 * Build payload for adding/updating public transport eligibility rule.
 *
 * @param {string} field - The field being updated.
 * @param {any} value - The value to set.
 * @returns {Array|null} - Array containing payload object or null if field is invalid.
 */
export const buildAddUpdateEligibilityRulePayload = (field, value) => {
  const ruleId = ELIGIBILITY_RULE_ID[field];
  if (!ruleId) return null;

  return [
    {
      id: ruleId,
      name: 'Public Transport Eligibility Rules',
      isActive: true,
      conditions: [
        {
          fact: ELIGIBILITY_RULE_FACT[field] ?? '',
          operator: ELIGIBILITY_RULE_OPERATOR[field] ?? 'greaterThan',
          value,
          dataIdentifierField: field,
        },
      ],
      event: {
        type:
          ELIGIBILITY_RULE_EVENT_TYPE[field] ??
          ELIGIBILITY_RULE_EVENT_TYPE.default,
        params: {
          message: ELIGIBILITY_RULE_EVENT_MESSAGE[field] ?? '',
        },
      },
      priority: 1,
    },
  ];
};
export const USER_MANAGED_LABEL_GRID_LOCAL_STORAGE_KEY =
  'table-settings/user-managed-labels';
